<template>
    <div class="container">
        <b-modal id="openAgentFixReserveModal" title="固定予約を設定する" size="xl" ok-only ok-title="閉じる"
            dialog-class="custom-wide-modal" modal-class="agent-netenglish-modal" @show="onModalShow" @shown="onModalShown">

            <div class="alert alert-info" role="alert">
                最初に受講生を選択してください（検索または受講生ID）。その後、講師を選び、
                <strong>予約可能枠を表示する</strong>を押して、固定予約の設定または解除を行ってください。
            </div>

            <div class="alert alert-warning small" role="alert">
                <p class="font-weight-bold mb-2">固定レッスン予約（Fix Reserve）</p>
                <p class="mb-2">固定レッスン予約は、毎週同じ曜日・時間のレッスン枠を設定する機能です。</p>
                <p class="mb-2">固定予約を設定すると、すでに公開されているスケジュールのうち、空いている該当枠はその場で予約されます。</p>
                <p class="mb-2">すでに他の受講生が予約している日時は予約できません。その場合は、必要に応じて通常の「レッスン予約」画面から、別の講師または別の時間を予約してください。</p>
                <p class="mb-0">まだ公開されていない今後の枠は、次回以降のスケジュール作成時に、固定予約として自動的に予約を試みます。</p>
            </div>

            <div class="row mt-2">
                <div class="col-md-3">
                    <label>受講生</label>
                </div>
                <div class="col-md-9">
                    <multiselect style="color:#000; font-size:12px" ref="membersSelection" v-model="memberSelectedID"
                        :options="memberOptionList" placeholder="-- 受講生を選択 --" track-by="id" label="name"
                        @input="onMemberChange">
                        <template slot="singleLabel" slot-scope="{ option }">
                            <strong style="color:#000; font-size:12px">{{ option.name }}</strong>
                        </template>
                    </multiselect>
                </div>
            </div>

            <div id="multi-account-field-wrapper" class="row mt-2" v-show="showMultiAccountDropdown">
                <div class="col-md-3">受講生アカウント:</div>
                <div class="col-md-9">
                    <select id="multi-account-field" class="form-control form-control-sm" v-model="multiAccountID">
                        <option v-for="account in accounts" :key="'account-' + account.member_multi_account_id"
                            :value="account.member_multi_account_id">
                            AC{{ account.member_multi_account_id }} - {{ account.name }}
                        </option>
                    </select>
                </div>
            </div>

            <hr />

            <div class="row mt-2">
                <div class="col-md-6">
                    <label>講師</label>
                    <multiselect style="color:#000; font-size:12px" v-model="tutorSelected" :options="tutorOptionList"
                        placeholder="-- 講師を選択 --" track-by="user_id" label="name" :disabled="!memberID"
                        @input="onTutorChange">
                        <template slot="singleLabel" slot-scope="{ option }">
                            <strong style="color:#000; font-size:12px">{{ option.name }}</strong>
                        </template>
                    </multiselect>
                </div>

                <div class="col-md-3 d-flex align-items-end">
                    <button type="button" class="btn btn-primary btn-sm" :disabled="!canLoadSlots" @click="loadSlots">
                        予約可能枠を表示する
                    </button>
                </div>
            </div>

            <hr />

            <div class="row">
                <div class="col-12">
                    <h6 class="font-weight-bold">受講生の現在の固定予約</h6>
                    <p v-if="memberID && fixReserveLimitNormal !== null" class="small text-muted mb-2">
                        通常固定予約の利用状況：
                        <strong>{{ normalFixReserveUsed }}件 / 上限{{ fixReserveLimitNormal }}件</strong>
                    </p>
                    <div
                        v-if="isNormalFixReserveLimitReached"
                        class="alert alert-warning small mb-2"
                        role="alert"
                    >
                        この受講生は固定予約の上限に達しています。新しい固定予約を設定する場合は、既存の固定予約を解除してください。
                    </div>
                    <div v-if="!memberID" class="text-muted">受講生を選択してください。</div>
                    <div v-else-if="mySlots.length === 0" class="text-muted">ありません。</div>
                    <div v-else class="table-responsive">
                        <table class="table table-sm table-bordered">
                            <thead>
                                <tr>
                                    <th>曜日</th>
                                    <th>時間</th>
                                    <th>講師</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="s in mySlots" :key="'my-' + s.id">
                                    <td>{{ dayLabel(s.day_of_week) }}</td>
                                    <td>{{ s.time_slot }}</td>
                                    <td>{{ tutorLabel(s) }}</td>
                                    <td class="text-center">
                                        <button type="button" class="btn btn-danger btn-sm" @click="removeSlot(s)">
                                            解除
                                        </button>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            <div class="row mt-3">
                <div class="col-12">
                    <h6 class="font-weight-bold">予約可能な枠一覧</h6>
                    <div v-if="!memberID" class="text-muted">受講生を選択してください。</div>
                    <div v-else-if="!tutorID" class="text-muted">講師を選択して「予約可能枠を表示する」を押してください。</div>
                    <div v-else-if="slots.length === 0" class="text-muted">対象の枠はありません。</div>
                    <div v-else class="table-responsive">
                        <table class="table table-sm table-bordered">
                            <thead>
                                <tr>
                                    <th style="min-width: 80px;">時間</th>
                                    <th v-for="d in dayOptions" :key="'h-' + d.value" style="min-width: 92px;">
                                        {{ d.shortLabel }}
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="t in timeSlots" :key="'t-' + t">
                                    <th class="align-middle">{{ t }}</th>
                                    <td v-for="d in dayOptions" :key="'c-' + d.value + '-' + t">
                                        <template v-if="slotMap[d.value] && slotMap[d.value][t]">
                                            <div class="d-flex flex-column">
                                                <div>
                                                    <span v-if="slotMap[d.value][t].is_open"
                                                        class="text-success font-weight-bold">空き</span>
                                                    <span v-else-if="isMine(slotMap[d.value][t])"
                                                        class="text-primary font-weight-bold">予約済み</span>
                                                    <span v-else class="text-muted">{{ unavailableLabel(slotMap[d.value][t]) }}</span>
                                                </div>
                                                <div class="mt-1">
                                                    <button v-if="slotMap[d.value][t].is_open" type="button"
                                                        class="btn btn-primary btn-sm" :disabled="!canBook"
                                                        @click="bookSlot(slotMap[d.value][t])">
                                                        予約
                                                    </button>
                                                    <span v-else class="text-muted">—</span>
                                                </div>
                                            </div>
                                        </template>
                                        <template v-else>
                                            <span class="text-muted">—</span>
                                        </template>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

        </b-modal>
    </div>
</template>

<script>
import Multiselect from 'vue-multiselect'

export default {
    components: {
        Multiselect
    },
    props: {
        agent_info: Object,
        agent_user_id: Number,
        api_token: String,
    },
    data() {
        return {
            memberSelectedID: null,
            memberOptionList: [],
            pendingMemberUserId: null,
            memberID: null,

            accounts: [],
            multiAccountID: null,
            showMultiAccountDropdown: false,

            tutorSelected: null,
            tutorOptionList: [],
            tutorID: null,

            dayOptions: [
                { value: 1, label: '月曜日', shortLabel: '月' },
                { value: 2, label: '火曜日', shortLabel: '火' },
                { value: 3, label: '水曜日', shortLabel: '水' },
                { value: 4, label: '木曜日', shortLabel: '木' },
                { value: 5, label: '金曜日', shortLabel: '金' },
                { value: 6, label: '土曜日', shortLabel: '土' },
                { value: 7, label: '日曜日', shortLabel: '日' },
            ],
            timeSlots: [
                '11:00', '11:30', '12:00', '12:30', '13:00', '13:30', '14:00', '14:30',
                '15:00', '15:30', '16:00', '16:30', '17:00', '17:30', '18:00', '18:30',
                '19:00', '19:30', '20:00', '20:30', '21:00', '21:30', '22:00', '22:30',
                '23:00', '23:30', '24:00', '24:30',
            ],

            slots: [],
            mySlots: [],

            fixReserveLimitNormal: null,
            normalFixReserveUsed: 0,
        }
    },
    computed: {
        canLoadSlots() {
            return !!this.memberID && !!this.tutorID;
        },
        isNormalFixReserveLimitReached() {
            if (this.fixReserveLimitNormal === null) {
                return false;
            }
            return this.normalFixReserveUsed >= this.fixReserveLimitNormal;
        },
        canBook() {
            if (!this.memberID) return false;
            if (this.isNormalFixReserveLimitReached) return false;
            if (this.showMultiAccountDropdown && (this.multiAccountID === null || this.multiAccountID === '')) {
                return false;
            }
            return true;
        },
        slotMap() {
            const map = {
                1: {}, 2: {}, 3: {}, 4: {}, 5: {}, 6: {}, 7: {}
            };

            for (const s of (this.slots || [])) {
                const d = parseInt(s.day_of_week, 10);
                const t = s.time_slot;
                if (!map[d]) map[d] = {};
                map[d][t] = s;
            }
            return map;
        },
    },
    methods: {
        openAgentFixReserveModal() {
            this.pendingMemberUserId = null;
            this.resetState();
            this.getAgentMemberList();
            this.getTutorsList();
            this.$bvModal.show('openAgentFixReserveModal');
        },
        openAgentFixReserveModalForMember(memberUserId) {
            this.pendingMemberUserId = memberUserId;
            this.resetState();
            this.memberOptionList = [];
            this.getTutorsList();
            this.$bvModal.show('openAgentFixReserveModal');
            this.getAgentMemberList().then(() => {
                this.$nextTick(() => this.applyPendingMemberSelection());
            });
        },
        selectMemberByUserId(userId) {
            const option = this.memberOptionList.find(
                (member) => String(member.id) === String(userId)
            );

            if (!option) {
                return false;
            }

            this.memberSelectedID = option;
            this.onMemberChange(option);
            return true;
        },
        applyPendingMemberSelection() {
            if (!this.pendingMemberUserId) {
                return;
            }

            const pendingId = this.pendingMemberUserId;

            if (this.selectMemberByUserId(pendingId)) {
                this.pendingMemberUserId = null;
                return;
            }

            this.getAgentMemberList().then(() => {
                this.$nextTick(() => {
                    if (this.selectMemberByUserId(pendingId)) {
                        this.pendingMemberUserId = null;
                    }
                });
            });
        },
        onModalShow() {
            if (this.pendingMemberUserId) {
                return;
            }

            if (this.memberOptionList.length === 0) {
                this.getAgentMemberList();
            }
            if (this.tutorOptionList.length === 0) {
                this.getTutorsList();
            }
        },
        onModalShown() {
            if (this.pendingMemberUserId) {
                this.$nextTick(() => this.applyPendingMemberSelection());
            }
        },
        resetState() {
            this.memberSelectedID = null;
            this.memberID = null;
            this.resetMultiAccount();
            this.tutorSelected = null;
            this.tutorID = null;
            this.slots = [];
            this.mySlots = [];
            this.fixReserveLimitNormal = null;
            this.normalFixReserveUsed = 0;
        },
        resetMultiAccount() {
            this.showMultiAccountDropdown = false;
            this.multiAccountID = null;
            this.accounts = [];
        },
        getAgentMemberList() {
            return axios.post("/api/getAgentMembers?api_token=" + this.api_token, {
                method: "POST",
                message: "",
                agent_user_id: this.$props.agent_user_id,
            }).then(response => {
                const options = [];
                if (response.data.success === true) {
                    response.data.members.forEach((member) => {
                        options.push({
                            id: member.uid,
                            firstname: member.fn,
                            lastname: member.ln,
                            name: `${member.uid} ${member.fn} ${member.ln}`.trim()
                        });
                    });
                }
                this.memberOptionList = options;
                return options;
            });
        },
        getTutorsList() {
            axios.post("/api/getTutorList?api_token=" + this.api_token, {
                method: "POST",
                for_fix_reserve: true,
            }).then(response => {
                if (response.data.success === false) {
                    alert(response.data.message);
                    return;
                }

                const tutors = response.data.tutors || [];
                const options = tutors.map(t => ({
                    user_id: t.user_id,
                    name: (t.firstname || '') + ' ' + (t.lastname || '')
                }));
                this.tutorOptionList = options;
            }).catch(function (error) {
                alert("Error " + error);
            });
        },
        onMemberChange(value) {
            this.resetMultiAccount();
            this.slots = [];
            this.mySlots = [];
            this.fixReserveLimitNormal = null;
            this.normalFixReserveUsed = 0;

            try {
                this.memberID = value.id;
            } catch (err) {
                this.memberID = null;
            }

            if (this.memberID) {
                this.showMultiAccountField(this.memberID);
                this.loadMySlots();
            }
        },
        onTutorChange(value) {
            this.slots = [];
            try {
                this.tutorID = value.user_id;
            } catch (err) {
                this.tutorID = null;
            }
        },
        showMultiAccountField(memberID) {
            this.showMultiAccountDropdown = false;

            axios.post("/api/getMultiAccountOptions?api_token=" + this.api_token, {
                method: "POST",
                memberID: memberID,
            }).then(response => {
                this.accounts = response.data.accounts || [];

                if (this.accounts.length > 0) {
                    this.showMultiAccountDropdown = true;

                    for (const account of this.accounts) {
                        if (account.is_default == true || account.is_default == 1 || account.is_default == "true") {
                            this.multiAccountID = account.member_multi_account_id;
                            this.$forceUpdate();
                        }
                    }
                }
            });
        },
        loadMySlots() {
            if (!this.memberID) return;

            axios.post("/api/agent/fixReserve/mySlots?api_token=" + this.api_token, {
                method: "POST",
                memberID: this.memberID,
            }).then(response => {
                if (response.data.success) {
                    this.mySlots = response.data.slots || [];
                    const lim = response.data.fix_reserve_limit_normal;
                    this.fixReserveLimitNormal = typeof lim === 'number' ? lim : parseInt(lim, 10);
                    if (Number.isNaN(this.fixReserveLimitNormal)) {
                        this.fixReserveLimitNormal = null;
                    }
                    const used = response.data.normal_fix_reserve_used;
                    this.normalFixReserveUsed = typeof used === 'number' ? used : parseInt(used, 10) || 0;
                } else {
                    alert(response.data.message || '固定予約情報の取得に失敗しました');
                }
            }).catch(() => alert('固定予約情報の取得に失敗しました'));
        },
        loadSlots() {
            if (!this.memberID) {
                alert('受講生を選択してください');
                return;
            }
            if (!this.tutorID) {
                alert('講師を選択してください');
                return;
            }

            axios.post("/api/agent/fixReserve/slots?api_token=" + this.api_token, {
                method: "POST",
                memberID: this.memberID,
                tutor_id: this.tutorID,
                day_of_week: null,
            }).then(response => {
                if (response.data.success) {
                    this.slots = response.data.slots || [];
                } else {
                    alert(response.data.message || '枠の取得に失敗しました');
                }
            }).catch(() => alert('枠の取得に失敗しました'));
        },
        isMine(slot) {
            return parseInt(slot.assigned_member_id, 10) === parseInt(this.memberID, 10);
        },
        unavailableLabel(slot) {
            const nickname = slot && slot.assigned_member_nickname
                ? String(slot.assigned_member_nickname).trim()
                : '';
            return nickname ? ('受付不可（' + nickname + '）') : '受付不可';
        },
        dayLabel(day) {
            const found = this.dayOptions.find(d => parseInt(d.value, 10) === parseInt(day, 10));
            return found ? found.label : day;
        },
        tutorLabel(slot) {
            if (!slot || !slot.tutor) return '-';
            return slot.tutor.japanese_firstname || slot.tutor.firstname || '-';
        },
        showPopup(icon, message) {
            const msg = message || '';
            if (typeof Swal !== 'undefined' && Swal && typeof Swal.fire === 'function') {
                return Swal.fire({
                    icon: icon || 'info',
                    html: String(msg).replace(/\n/g, '<br>'),
                    confirmButtonText: 'OK',
                });
            }
            alert(msg);
            return Promise.resolve();
        },
        showBookingResult(data) {
            const msg = data.message || data.message_en || '固定予約を設定しました。';
            const hasUnavailable = (data.unavailable || []).length > 0;
            const body = String(msg)
                .replace(/^固定予約を設定しました。?\s*/u, '')
                .replace(/^Fix reserve has been registered\.?\s*/i, '')
                .trim();
            const html = '<div class="text-left">' + (body || msg).replace(/\n/g, '<br>') + '</div>';

            if (typeof Swal !== 'undefined' && Swal && typeof Swal.fire === 'function') {
                const opts = {
                    icon: 'success',
                    title: '固定予約を設定しました',
                    html: html,
                    confirmButtonText: 'OK',
                };
                if (hasUnavailable) {
                    opts.showDenyButton = true;
                    opts.denyButtonText = '通常予約画面へ進む';
                }
                return Swal.fire(opts).then((result) => {
                    this.loadSlots();
                    this.loadMySlots();
                    if (result && result.isDenied && typeof window.openCalendarReservationModal === 'function') {
                        this.$bvModal.hide('openAgentFixReserveModal');
                        window.openCalendarReservationModal();
                    }
                });
            }

            alert(msg);
            this.loadSlots();
            this.loadMySlots();
            if (hasUnavailable && window.confirm('通常予約画面へ進みますか？') && typeof window.openCalendarReservationModal === 'function') {
                this.$bvModal.hide('openAgentFixReserveModal');
                window.openCalendarReservationModal();
            }
            return Promise.resolve();
        },
        buildPreviewHtml(data) {
            let html = '';
            const instant = (data && data.instant) || [];
            const unavailable = (data && data.unavailable) || [];

            html += '<div class="text-left mb-2">以下の内容で固定予約を設定します。</div>';

            if (instant.length > 0) {
                html += '<div class="text-left mb-2"><strong>今回すぐに予約される日時</strong><ul class="mb-0 pl-3">';
                instant.forEach((row) => {
                    html += '<li>' + (row.label || row.lesson_time) + '</li>';
                });
                html += '</ul></div>';
            } else {
                html += '<div class="text-left mb-2">今回すぐに予約できる公開済みの枠はありません。</div>';
            }

            if (unavailable.length > 0) {
                html += '<div class="text-left mb-2"><strong>すでに予約済みのため、今回予約できない日時</strong><ul class="mb-0 pl-3">';
                unavailable.forEach((row) => {
                    html += '<li>' + (row.label || row.lesson_time) + '</li>';
                });
                html += '</ul><div class="small text-muted mt-1">上記の予約できない日時については、必要に応じて通常の「レッスン予約」画面から、別の講師または別の時間を予約してください。</div></div>';
            }

            html += '<div class="text-left mb-0 small">まだ公開されていない今後の枠は、次回以降のスケジュール作成時に、固定予約として自動的に予約を試みます。</div>';

            return html;
        },
        bookSlot(slot) {
            if (!this.memberID) {
                this.showPopup('warning', '受講生を選択してください');
                return;
            }
            if (this.showMultiAccountDropdown && (this.multiAccountID === null || this.multiAccountID === '')) {
                this.showPopup('warning', '受講生アカウントを選択してください');
                return;
            }
            if (this.isNormalFixReserveLimitReached) {
                this.showPopup('warning', 'この受講生は固定予約の上限に達しています。新しい固定予約を設定する場合は、既存の固定予約を解除してください。');
                return;
            }

            const apiToken = this.api_token;
            const payloadBase = {
                memberID: this.memberID,
                fix_reserve_slot_id: slot.id,
                member_multi_account_id: this.multiAccountID,
            };

            axios.post("/api/agent/fixReserve/preview?api_token=" + apiToken, payloadBase)
                .then(previewRes => {
                    const data = previewRes.data || {};
                    if (!data.success) {
                        this.showPopup('error', data.message || data.message_en || '確認に失敗しました');
                        return;
                    }

                    const html = this.buildPreviewHtml(data);
                    const confirmPreview = (typeof Swal !== 'undefined' && Swal && typeof Swal.fire === 'function')
                        ? Swal.fire({
                            icon: 'info',
                            title: '固定予約の確認',
                            html: html,
                            showCancelButton: true,
                            confirmButtonText: 'この内容で固定予約を登録する',
                            cancelButtonText: 'キャンセル',
                        })
                        : Promise.resolve({
                            isConfirmed: window.confirm((data.message || '') + '\n\nこの内容で固定予約を登録します。よろしいですか？'),
                        });

                    return confirmPreview.then((result) => {
                        if (!result || !result.isConfirmed) {
                            return;
                        }

                        return axios.post("/api/agent/fixReserve/book?api_token=" + apiToken, Object.assign({
                            method: "POST",
                        }, payloadBase)).then(response => {
                            if (response.data.success) {
                                this.showBookingResult(response.data);
                            } else {
                                this.showPopup('error', response.data.message || response.data.message_en || '予約に失敗しました');
                            }
                        });
                    });
                })
                .catch(() => this.showPopup('error', '予約に失敗しました'));
        },
        removeSlot(slot) {
            if (!this.memberID) {
                this.showPopup('warning', '受講生を選択してください');
                return;
            }

            const confirmRemove = (typeof Swal !== 'undefined' && Swal && typeof Swal.fire === 'function')
                ? Swal.fire({
                    icon: 'warning',
                    title: '固定予約を解除しますか？',
                    text: '選択した受講生の固定予約を解除します。',
                    showCancelButton: true,
                    confirmButtonText: '解除する',
                    cancelButtonText: 'キャンセル',
                })
                : Promise.resolve({
                    isConfirmed: window.confirm('選択した受講生の固定予約を解除しますか？'),
                });

            confirmRemove.then((result) => {
                if (!result || !result.isConfirmed) {
                    return;
                }

                axios.post("/api/agent/fixReserve/remove?api_token=" + this.api_token, {
                    method: "POST",
                    memberID: this.memberID,
                    fix_reserve_slot_id: slot.id,
                }).then(response => {
                    if (response.data.success) {
                        this.showPopup('success', response.data.message || response.data.message_en || '固定予約を解除しました')
                            .then(() => {
                                this.loadSlots();
                                this.loadMySlots();
                            });
                    } else {
                        this.showPopup('error', response.data.message || response.data.message_en || '固定予約の解除に失敗しました');
                    }
                }).catch(() => this.showPopup('error', '固定予約の解除に失敗しました'));
            });
        },
    }
}
</script>

<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>

<style>
.custom-wide-modal {
    max-width: 95%;
    width: 95%;
}
</style>
